/**
 * useDeleteConfirmation - Gates canvas node deletion behind a confirm/cancel dialog.
 *
 * Both the keyboard handler (Delete/Backspace) and the right-click context menu used to
 * delete + persist nodes inline. That made an accidental keypress on a selected step
 * immediately fire an irreversible server DELETE. This hook centralizes deletion so both
 * entry points instead *request* a deletion, which opens a confirmation dialog. The actual
 * removal + API persistence only runs once the user confirms.
 *
 * Related:
 *   - hooks/useCanvasKeyboardShortcuts.ts - calls requestDeleteNodes on Delete/Backspace
 *   - hooks/useContextMenuActions.ts - calls requestDeleteNodes from the menu
 *   - components/DeleteStepConfirmationDialog.tsx - the confirm/cancel UI
 *   - utils/nodeDelete.ts - the underlying deleteNodes / branch-case planning
 */

import type { Edge, Node } from '@xyflow/react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { BaseNodeData } from '../types';
import { buildBranchCaseDeletionPlan, deleteNodes, isProtectedNode } from '../utils/nodeDelete';
import type { DeletedStepInfo } from '../utils/nodeDelete';
import type { HistoryActionType } from './useUndoRedo';

/**
 * Snapshot of a deletion the user has requested but not yet confirmed.
 */
export interface PendingDelete {
	/** React Flow node IDs the user asked to delete (may include protected nodes). */
	nodeIds: string[];
	/** Number of nodes that will actually be removed (protected nodes excluded). */
	count: number;
	/** Label of the single node being deleted, when exactly one is targeted. */
	primaryLabel?: string;
}

interface UseDeleteConfirmationParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	/** Persist step deletions to the API. */
	onDeleteSteps?: (steps: DeletedStepInfo[]) => Promise<void>;
	/** Persist segment-branch deletions to the API. */
	onDeleteBranchCases?: (cases: { switchNodeId: string; caseId: string }[]) => Promise<void>;
}

interface UseDeleteConfirmationResult {
	/** Non-null while a deletion is awaiting confirmation. */
	pendingDelete: PendingDelete | null;
	/** Open the confirmation dialog for the given node IDs (no-op for protected-only selections). */
	requestDeleteNodes: (nodeIds: string[]) => void;
	/** Execute the pending deletion and persist it to the server. */
	confirmDelete: () => void;
	/** Dismiss the pending deletion without changing anything. */
	cancelDelete: () => void;
}

const PROTECTED_ONLY_MESSAGE =
	'This node cannot be deleted directly (triggers, default segments, and behavior branches are protected).';

export function useDeleteConfirmation({
	nodes,
	edges,
	setNodes,
	setEdges,
	takeSnapshot,
	onDeleteSteps,
	onDeleteBranchCases,
}: UseDeleteConfirmationParams): UseDeleteConfirmationResult {
	const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

	const requestDeleteNodes = useCallback(
		(nodeIds: string[]) => {
			if (nodeIds.length === 0) {
				return;
			}

			// Only count nodes that can actually be deleted, so we never pop a confirmation
			// for a selection that consists solely of protected nodes (triggers / default branches).
			const deletableNodes = nodeIds
				.map(id => nodes.find(node => node.id === id))
				.filter((node): node is Node<BaseNodeData> => node !== undefined && !isProtectedNode(node, false));

			if (deletableNodes.length === 0) {
				toast.info(PROTECTED_ONLY_MESSAGE);
				return;
			}

			setPendingDelete({
				nodeIds,
				count: deletableNodes.length,
				primaryLabel: deletableNodes.length === 1 ? (deletableNodes[0].data.label as string | undefined) : undefined,
			});
		},
		[nodes]
	);

	const cancelDelete = useCallback(() => {
		setPendingDelete(null);
	}, []);

	const confirmDelete = useCallback(() => {
		if (!pendingDelete) {
			return;
		}

		const { nodeIds } = pendingDelete;

		// Segment-branch deletions are persisted through their case-delete endpoint; collect
		// that plan up-front so those steps are not also sent to the per-step delete endpoint.
		const { casesToDelete, skipStepDeletionNodeIds } = buildBranchCaseDeletionPlan(nodeIds, nodes, edges);
		const result = deleteNodes(nodeIds, nodes, edges);

		if (result.deletedCount > 0) {
			takeSnapshot('node_delete', `Delete ${result.deletedCount} node(s)`);
			setNodes(result.nodes);
			setEdges(result.edges);

			if (onDeleteBranchCases && casesToDelete.length > 0) {
				onDeleteBranchCases(casesToDelete).catch(() => {
					toast.error('Failed to persist branch deletion to server');
				});
			}

			if (onDeleteSteps && result.deletedSteps.length > 0) {
				const stepsToPersist = result.deletedSteps.filter(step => !skipStepDeletionNodeIds.has(step.nodeId || ''));
				if (stepsToPersist.length > 0) {
					onDeleteSteps(stepsToPersist).catch(() => {
						toast.error('Failed to persist step deletion to server');
					});
				}
			}
		}

		setPendingDelete(null);
	}, [pendingDelete, nodes, edges, setNodes, setEdges, takeSnapshot, onDeleteSteps, onDeleteBranchCases]);

	return {
		pendingDelete,
		requestDeleteNodes,
		confirmDelete,
		cancelDelete,
	};
}
